import { useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import humps from 'humps';
import { API } from "./apiHook";

/**
 * Custom hook to execute API requests with automatic cancellation of in-flight requests 
 * if it would called infinite times.
 */
export const useCancelableApi = () => {
    const abortControllerRef = useRef(null);

    // Clean up any pending request on component unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const callApi = useCallback(async ({
        endpoint,
        method = 'GET',
        params = {},
        data = null, // we can use this for POST, PUT, PATCH api requests
        headers = {},
        transformCamelCase = true,
        accessToken = null
    }) => {
        // Abort previous pending request for this hook instance
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Creating a new AbortController instance
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            // Construct URL Query Parameters safely
            const searchParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    searchParams.append(key, value);
                }
            });

            const queryString = searchParams.toString();
            // if the query parameter (params) of api is empty then 
            // simple api end point will be called
            const fullApiEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;

            // Set Headers
            const requestHeaders = {
                'Content-Type': 'application/json',
                ...(accessToken && { Authorization: `Token ${accessToken}` }),
                ...headers
            };

            // Execute Axios Request
            const response = await API({
                url: fullApiEndpoint,
                method,
                data,
                headers: requestHeaders,
                signal: controller.signal
            });

            // Clear controller ref if api called finished successfully
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }

            // Return parsed data (CamelCase conversion optional)
            return transformCamelCase ? humps.camelizeKeys(response) : response;
        } catch (error) {
            // Handle Abort / Cancellation quietly
            if (axios.isCancel(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
                console.log(`Request canceled for endpoint:`, endpoint);
                return { isCanceled: true };
            }

            // Clean ref on actual error
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }

            throw error;
        }
    },
        []
    );

    const cancelRequest = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, []);

    return { callApi, cancelRequest };
};